// Card 06, the cartouche (R548, ui-map-interface.md §17.3).
//
// A chart's title block names the sheet and the survey, and it carries the
// scale bar. This one is what the SEASON BANNER becomes when it is not
// announcing: the world and its region, the season and its day, the clock
// that runs now, and a scale bar of five cells that stretches and shrinks
// with the zoom. The datum line of the sonar unit and the sov turnover went
// on the owner's word of 2026-10-09 (R1852).
//
// **It is the banner's resting form.** `SeasonBanner.raise` hides the
// cartouche and `SeasonBanner.clear` shows it again, through the two calls
// below. The two must never stand on the glass together: the banner is on
// the map for four and a half seconds, and the cartouche for the rest of
// the session.
//
// **It is the plate at the foot of the bottom-right stack** (R556, R1517,
// round 086 cards 05 a and 08): left of the empire dock, on the corner
// grid, and the lowest card of `stack.ts`. It wears the one chart card
// frame (`frame.ts`, card 04). Its minimize control folds the plate to its
// head row for the session. The imprint is no part of the plate: it is ink
// in the margin past the corner of the world (card 24, R1852).
//
// **The season line names the clock that runs** (R556): the founding truce
// while it holds, the seaquake's stage once one runs, and nothing after a
// truce that ended in a calm sea. The stage is its one name (`quakeState`,
// R2090) with no clock, so the line fits on one row (R2112); the top bar
// counts the stage.
//
// **The bar is the only live figure.** The words change on the minute and
// on a hello; the bar's width is `cells · COL_PITCH · zoom` and moves with
// the camera. Every write below is guarded by a compare, so a frame that
// changed nothing writes no style and no text at all (§10.3).
//
// **The scale row is always one line** (R589). The bar holds `scale_cells`
// cells. When the row cannot hold the bar, the zero and the words together,
// the bar sheds one cell at a time down to `scale_cells_min`. The piece
// measures the row only in the frame that moved the bar, so the cost of the
// measurement is nothing on a still camera. A count is proved with its own
// words before it lands, so the row never flips between two counts at one
// zoom (R630).
//
// **The plate places the stack only when it moved** (R2245). A placement
// reads the height of every card of the stack, which forces a layout. The
// plate renders on every state frame, so it places the stack only on the
// first render, a new title, season line or scale words, a new reading, a
// moved bar, a window resize and a change of the notice band, whose foot
// is the ceiling of the column. The cards stand where they stood before.

import { COL_PITCH } from '../../gen/balance';
import { CHART } from '../../gen/presentation';
import { chart as S } from '../../gen/strings';
import { formatEta } from '../../render/bearing';
import { toRoman } from '../../render/roman';
import { quakeState } from '../format';
import type { ChartContext, DomPiece } from '../../fast/chart/context';
import { chartCard } from '../../fast/chart/dom';
import { cardHead, stepClass } from './frame';
import { restack, stackCard, unstackCard } from './stack';
import { seasonDay } from '../../fast/chart/pieces/shared';

// ---------------------------------------------------------------------------
// The banner's rest hook
// ---------------------------------------------------------------------------
//
// `ui/banner.ts` calls `setBannerShowing` on both edges of a card's life.
// The flag is module state and not a constructor argument, because the
// banner is built by `main.ts` and the piece by the chart registry: neither
// of the two can hand the other a function without a third owner.

let bannerUp = false;

/** The banner raised a card, or cleared one. The cartouche follows it. */
export function setBannerShowing(up: boolean): void { bannerUp = up; }

/** True while the banner holds the map, so the cartouche is not on the glass. */
export function bannerShowing(): boolean { return bannerUp; }

/** The plate is folded to its head row: the session's answer to its
 *  minimize control. It is not a setting, so a reload opens it again. */
let folded = false;

/** The parts of the card, so a write can find its own element. */
interface Parts {
  head: HTMLElement;
  title: HTMLElement;
  sub: HTMLElement;
  row: HTMLElement;
  zero: HTMLElement;
  bar: HTMLElement;
  length: HTMLElement;
}

/**
 * How many cells the bar can show in `availPx` of room, at this zoom.
 *
 * The answer is the largest count from `cells` down to `minCells` whose bar
 * width goes into the room. When even `minCells` is too wide, the count is
 * `minCells`: the bar then hides its own overflow, and the row stays one
 * line (R589).
 */
export function fitScaleCells(
  availPx: number, zoom: number, cells: number, minCells: number,
): number {
  const top = Math.max(1, Math.floor(cells));
  const min = Math.min(top, Math.max(1, Math.floor(minCells)));
  for (let n = top; n > min; n--) {
    if (Math.round(n * COL_PITCH * zoom) <= availPx) return n;
  }
  return min;
}

export class Cartouche implements DomPiece {
  readonly id = 'cartouche' as const;
  readonly tier = 'dom' as const;
  readonly film = 'hide' as const;
  readonly rebuildOn = [
    'hello', 'state', 'minute', 'reading', 'layout', 'resize',
  ] as const;

  private el: HTMLElement | null = null;
  private parts: Parts | null = null;
  private readonly said = new Map<string, string>();
  private lastBar = -1;
  private lastFull = -1;
  private lastCells = -1;
  private gapPx = -1;
  private measureAgain = false;
  private lastReading = '';
  /** The stack must be placed again at the next render (R2245). */
  private restackDue = true;
  /** The notice band the plate watches, and its size watch. */
  private band: HTMLElement | null = null;
  private bandWatch: ResizeObserver | null = null;
  private readonly onResize = (): void => { this.restackDue = true; };

  /** The banner owns the map while it is up. */
  visible(_ctx: ChartContext): boolean { return !bannerUp; }

  mount(root: HTMLElement, _ctx: ChartContext): void {
    const t = CHART.pieces.cartouche;
    const cells = Math.max(1, Math.floor(t.fig.scale_cells));
    const el = chartCard('cartouche');
    const head = this.headOf();
    el.appendChild(head);
    el.insertAdjacentHTML('beforeend',
      `<div class="ct-body"><div class="ct-sub ${stepClass(t.text.season)}"></div>`
      + `<div class="ct-scale"><span class="ct-zero ${stepClass(t.text.scale)}">0</span>`
      + `<span class="ct-bar">${'<i></i>'.repeat(cells)}</span>`
      + `<span class="ct-len ${stepClass(t.text.scale)}"></span></div></div>`);
    this.el = el;
    this.parts = {
      head,
      title: head.querySelector('.cc-title')!,
      sub: el.querySelector('.ct-sub')!,
      row: el.querySelector('.ct-scale')!,
      zero: el.querySelector('.ct-zero')!,
      bar: el.querySelector('.ct-bar')!,
      length: el.querySelector('.ct-len')!,
    };
    this.said.clear();
    this.lastBar = -1;
    this.lastFull = -1;
    this.lastCells = cells;
    this.gapPx = -1;
    this.measureAgain = false;
    this.lastReading = '';
    this.restackDue = true;
    this.band = null;
    window.addEventListener('resize', this.onResize);
    el.classList.toggle('folded', folded);
    root.appendChild(el);
  }

  /**
   * Watches the notice band (R2245): a band that stands up, is replaced or
   * changes its size moves the ceiling of the column, so the stack is
   * placed again at the next render. A test document has no size watch,
   * so there the plate sees a new band and no change of its size.
   */
  private watchBand(): void {
    const band = document.getElementById('notices');
    if (band === this.band) return;
    this.band = band;
    this.restackDue = true;
    this.bandWatch?.disconnect();
    this.bandWatch = null;
    if (band && typeof ResizeObserver !== 'undefined') {
      this.bandWatch = new ResizeObserver(() => { this.restackDue = true; });
      this.bandWatch.observe(band);
    }
  }

  /** The head row of the frame: the title and the fold control. */
  private headOf(): HTMLElement {
    return cardHead('', CHART.pieces.cartouche.text.title, {
      act: 'cartouche', title: S.cartouche_min_title, folded,
    });
  }

  /**
   * The plate's minimize control (card 04): folds the plate to its head
   * row, or opens it again. The overlay's delegate calls it, and the
   * plate answers at once, without waiting for a rebuild reason.
   */
  toggleFold(): void {
    folded = !folded;
    const el = this.el, p = this.parts;
    if (!el || !p) return;
    el.classList.toggle('folded', folded);
    const head = this.headOf();
    const title = head.querySelector<HTMLElement>('.cc-title')!;
    title.textContent = p.title.textContent;
    title.title = p.title.title;
    p.head.replaceWith(head);
    p.head = head;
    p.title = title;
    restack();
  }

  render(ctx: ChartContext): void {
    const el = this.el, p = this.parts;
    if (!el || !p) return;
    const t = CHART.pieces.cartouche;
    const hist = ctx.reading === 'historical';

    // The frame states the box and every size (card 04). The corner goes
    // through the stack below, once the words give the card its height.
    if (this.lastReading !== ctx.reading) {
      this.lastReading = ctx.reading;
      el.classList.toggle('historical', hist);
      // The face changed, so the room in the row changed with it.
      this.measureAgain = true;
      this.restackDue = true;
    }
    this.watchBand();

    const world = ctx.state.hello?.worldName ?? '';
    const region = ctx.state.hello?.region ?? '';
    const season = Math.max(1, Math.floor(ctx.state.hello?.season ?? 1));
    const day = seasonDay(ctx.state);

    // A world with no region (the theme preview) names the world alone, and
    // no separator ends the title (R2043).
    const title = hist
      ? S.cartouche_title_historical({ world })
      : region ? S.cartouche_title_modern({ world, region }) : world;
    const sub = seasonLine(ctx, hist, season, day);

    if (this.write(p.title, 'title', title)) this.restackDue = true;
    if (this.write(p.sub, 'sub', sub)) this.restackDue = true;
    // The scale row is one line, so the words set the height (R1517). The
    // corner goes through the stack: the card is its lowest. The stack is
    // placed only when something it reads moved (R2245).
    if (this.restackDue) {
      this.restackDue = false;
      stackCard('cartouche', el);
    }

    // The scale bar: the one figure the camera moves. The full bar's width
    // is the camera's own reading, and it is rounded to the whole px, so a
    // pan of a fraction of a px measures nothing and writes nothing.
    const full = Math.round(t.fig.scale_cells * COL_PITCH * ctx.zoom);
    if (full === this.lastFull && !this.measureAgain) return;
    this.lastFull = full;
    this.measureAgain = false;

    // The words state the count, so the room the words leave depends on
    // the count the words state. A candidate is therefore proved with ITS
    // OWN words before it lands: the words are written, the row is measured
    // again, and a candidate that no longer fits beside them shrinks. The
    // words grow with the count, so a smaller candidate always fits beside
    // its own words, and the loop settles in at most two measurements. It
    // settles in this frame and not the next, so no count can flip back
    // and forth at a zoom that sits on a boundary (R630).
    let cells = this.fit(p, ctx.zoom);
    for (let guard = 0; guard < 4 && cells !== this.lastCells; guard++) {
      this.write(p.length, 'length', S.cartouche_scale({
        cells, px: cells * COL_PITCH,
      }));
      const again = this.fit(p, ctx.zoom);
      if (again >= cells) break;
      cells = again;
    }
    let moved = false;
    if (cells !== this.lastCells) {
      this.lastCells = cells;
      p.bar.innerHTML = '<i></i>'.repeat(cells);
      moved = true;
    }
    if (this.write(p.length, 'length', S.cartouche_scale({
      cells, px: cells * COL_PITCH,
    }))) moved = true;

    const bar = Math.round(cells * COL_PITCH * ctx.zoom);
    if (bar !== this.lastBar) {
      this.lastBar = bar;
      p.bar.style.width = `${bar}px`;
      p.bar.style.height = `${t.fig.bar_px}px`;
      moved = true;
    }
    // A moved bar can change the width of the plate, which the floor of the
    // column reads, so the stack stands again at once (R2245).
    if (moved) restack();
  }

  /**
   * How many cells the bar shows now (R589).
   *
   * The room is the row less the zero, the words and the two column gaps.
   * A measurement of zero is no layout at all — a card not on the glass, or
   * a test — and the bar then keeps every cell.
   */
  private fit(p: Parts, zoom: number): number {
    const fig = CHART.pieces.cartouche.fig;
    const room = p.row.clientWidth;
    const zero = p.zero.offsetWidth;
    const words = p.length.offsetWidth;
    if (room <= 0 || zero <= 0 || words <= 0) return fig.scale_cells;
    if (this.gapPx < 0) {
      const g = parseFloat(getComputedStyle(p.row).columnGap);
      this.gapPx = Number.isFinite(g) ? g : 0;
    }
    const avail = room - zero - words - 2 * this.gapPx;
    return fitScaleCells(avail, zoom, fig.scale_cells, fig.scale_cells_min);
  }

  unmount(): void {
    this.el?.remove();
    this.el = null;
    this.parts = null;
    window.removeEventListener('resize', this.onResize);
    this.bandWatch?.disconnect();
    this.bandWatch = null;
    this.band = null;
    unstackCard('cartouche');
  }

  /** One text write, and only when the words changed; answers whether it
   *  wrote. The title ends in an ellipsis in a narrow card, so it carries
   *  its full words as its title too (R2043). */
  private write(el: HTMLElement, key: string, text: string): boolean {
    if (this.said.get(key) === text) return false;
    this.said.set(key, text);
    el.textContent = text;
    if (key === 'title') el.title = text;
    return true;
  }
}

/**
 * The season line: the season, the day, and the clock that runs now.
 *
 * The founding truce comes first while it holds. After it, the seaquake's
 * stage is named by its one name with no clock (R2112), and a calm sea
 * after the truce names nothing at all (R556).
 */
function seasonLine(ctx: ChartContext, hist: boolean, seasonN: number, dayN: number): string {
  const season = hist ? toRoman(seasonN) : String(seasonN);
  const day = hist ? toRoman(dayN) : String(dayN);
  const truce = ctx.state.truceLeftSec();
  if (truce > 0) {
    const left = formatEta(truce);
    return hist
      ? S.cartouche_season_truce_historical({ season, day, left })
      : S.cartouche_season_truce_modern({ season, day, left });
  }
  // The card phase names the new season, and a held world names no
  // clock in it, exactly as the timer line does (R2092).
  const stage = quakeState(
    ctx.state.board?.quakeStage ?? 0, ctx.state.settlementCard(), ctx.state.settlementHeld,
  );
  if (stage) {
    return hist
      ? S.cartouche_season_stage_historical({ season, day, stage })
      : S.cartouche_season_stage_modern({ season, day, stage });
  }
  return hist
    ? S.cartouche_season_historical({ season, day })
    : S.cartouche_season_modern({ season, day });
}

/** The one instance the registry holds. */
export const cartouche = new Cartouche();
